
import Vue from 'vue';
import Component from 'vue-class-component';
import WatermarkDialog from '../WatermarkDialog.vue';
import BeforeAfterMessages from './BeforeAfterMessages.vue';
import { Prop, Watch } from 'vue-property-decorator';
import SessionModule from '@/store/modules/Sessions';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import Watermark from '../../model/Watermark';
import Session from '../../model/Session';
import SessionListElement from '../../model/SessionListElement';
import api, { WelcomeMessageSetDto } from '../../service/api';
import LinkDialog from './LinkDialog.vue';
import PhotosModule from '../../store/modules/Photos';
import ConfirmDelete from './ConfirmDelete.vue';
import ConfirmationModel from '../ConfirmationModel';
import Agreements from '../Agreements.vue';
import Downloads from '../Downloads.vue';
import ClientLink from '@/model/ClientLinkListElement';
import Pricelists from '../price-list/Pricelists.vue';
import UserModule from '@/store/modules/User';
import PriceListsModule from '@/store/modules/PriceLists';
import WelcomeMessageSetsModule from '@/store/modules/WelcomeMessageSets';

const userModule = getModule(UserModule, store);
const sessionModule = getModule(SessionModule, store);
const photosModule = getModule(PhotosModule, store);
const priceListsModule = getModule(PriceListsModule, store);
const welcomeMessageSetsModule = getModule(WelcomeMessageSetsModule, store);

interface BeforeAfterMessagesPayload {
    title: string;
    welcomeMessage: string;
    purchaseMessage: string;
}

@Component({
    components: {
        WatermarkDialog,
        LinkDialog,
        ConfirmDelete,
        Agreements,
        Downloads,
        Pricelists,
        BeforeAfterMessages,
    },
})

export default class SessionSettings extends Vue {
    public visible = false;
    public currentTab: number | string | null = null;
    @Prop({
        required: true,
    })
    public session!: SessionListElement;
    public watermarkDialog: boolean = false;

    get editableWatermark() {
        return sessionModule.sessions[this.session.id]?.watermark || this.session.watermark;
    }

    public async openWatermarkDialog() {
        await sessionModule.loadSessionIfNotExists(this.session.id);
        this.watermarkDialog = true;
    }
    public singleSession = false;
    public multiSession = false;
    public requiresPayment = false;
    public confirmationModel = new ConfirmationModel();
    public links: ClientLink[] = [];
    public selectedPricelist: string|null = null;
    public priceListSelectionPending = false;
    public chatEnabled = false;
    public loading = false;
    public deletingSession = false;
    public beforeAfterMessagesDialog = false;
    public welcomeMessageSetId: string|null = null;
    public selectedBeforeAfterMessageOption = '__new__';
    public highlightPricelistSelection = false;
    public editingSessionName = false;
    public editedSessionName = '';
    public paymentWarningDismissed = false;
    public readonly createNewMessageOptionValue = '__new__';
    public readonly createNewPriceListOptionValue = '__new_price_list__';
    private highlightPricelistSelectionTimeout: number | null = null;
    private savingSessionName = false;
    public beforeAfterMessages: BeforeAfterMessagesPayload = {
        title: '',
        welcomeMessage: '',
        purchaseMessage: '',
    };

    get priceLists() {
        if (userModule.user) {
            return priceListsModule.priceListList || [];
        } else {
            return [];
        }
    }

    get sessionShared() {
        return this.links.some((link) => !link.disabled);
    }

    get welcomeMessageSets() {
        return welcomeMessageSetsModule.welcomeMessageSetList || [];
    }

    get beforeAfterMessageOptions() {
        return [
            { text: '◀ Utwórz nową', value: this.createNewMessageOptionValue },
            ...this.welcomeMessageSets.map((item) => ({ text: item.title, value: item.id })),
        ];
    }

    get priceListOptions() {
        if (this.priceLists.length === 0) {
            return [
                { text: '◀ Utwórz nowy', value: this.createNewPriceListOptionValue },
            ];
        }

        return this.priceLists.map((item) => ({ text: item.name, value: item.id }));
    }

    get priceListSelectLabel() {
        return this.priceLists.length === 0 ? '◀ Utwórz nowy' : 'Wybierz cennik';
    }

    get publicationConsentEnabled() {
        return (userModule.user as any)?.publicationConsent || false;
    }

    get paymentReceivingEnabled() {
        const user = userModule.user as any;
        return user?.paymentReceiving ?? user?.receivePayments ?? false;
    }

    get shouldShowPaymentWarning() {
        return !this.paymentReceivingEnabled && !this.paymentWarningDismissed;
    }

    get hasSelectedPricelist() {
        return !!this.selectedPricelist && this.selectedPricelist !== this.createNewPriceListOptionValue;
    }

    get isDownloadsTabActive() {
        return this.currentTab === 1 || this.currentTab === 'menu2';
    }

    get resolvedDefaultPriceList() {
        if (!this.selectedPricelist || this.selectedPricelist === this.createNewPriceListOptionValue) {
            return undefined;
        }
        return this.selectedPricelist;
    }

    get beforeAfterMessagesEnabled() {
        return !!this.welcomeMessageSetId;
    }

    public onPricelistButtonClick() {
        if (!this.paymentReceivingEnabled) {
            this.$root.$emit('openFinanceStuff', { highlight: true, target: 'paymentReceiving' });
        }
    }

    public dismissPaymentWarning() {
        this.paymentWarningDismissed = true;
    }

    public onBeforeAfterMessagesPlusClick() {
        this.beforeAfterMessagesDialog = true;
    }

    public onBeforeAfterMessagesSelectClick() {
        if (this.welcomeMessageSets.length === 0) {
            this.onBeforeAfterMessagesPlusClick();
        }
    }

    public startSessionNameEdit() {
        this.editedSessionName = this.session.name;
        this.editingSessionName = true;
        this.$nextTick(() => {
            const input = this.$refs.sessionNameInput as Vue | undefined;
            const nativeInput = input?.$el?.querySelector('input') as HTMLInputElement | null;
            nativeInput?.focus();
            nativeInput?.select();
        });
    }

    public cancelSessionNameEdit() {
        this.editingSessionName = false;
        this.editedSessionName = this.session.name;
    }

    public async saveSessionName() {
        if (!this.editingSessionName || this.savingSessionName) {
            return;
        }

        const trimmedName = this.editedSessionName.trim();
        if (!trimmedName) {
            this.editedSessionName = this.session.name;
            this.editingSessionName = false;
            return;
        }

        if (trimmedName === this.session.name) {
            this.editingSessionName = false;
            return;
        }

        this.savingSessionName = true;
        try {
            await sessionModule.loadSessionIfNotExists(this.session.id);
            const currentSession = sessionModule.sessions[this.session.id];
            if (!currentSession) {
                return;
            }

            await sessionModule.updateSession(new Session(
                currentSession.id,
                trimmedName,
                currentSession.watermark,
                currentSession.pricelistId,
                currentSession.finalizationEnabled,
                currentSession.downloadEnabled,
                currentSession.finalized,
                currentSession.finalizationMode,
                currentSession.orderCount,
                currentSession.chatEnabled,
                currentSession.welcomeMessageSetId,
            ));
            this.editedSessionName = trimmedName;
            this.editingSessionName = false;
        } finally {
            this.savingSessionName = false;
        }
    }

    public onPublicationConsentTabClick() {
        if (!this.publicationConsentEnabled) {
            this.$root.$emit('openFinanceStuffPublicationConsent');
        }
    }

    public async onShareTabClick(event?: Event) {
        if (this.hasSelectedPricelist) {
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.currentTab = 0;
        if (this.currentTab !== 0) {
            this.currentTab = 'menu1' as any;
        }
        await this.$nextTick();
        this.flashPricelistSelection();
    }

    public async changeWatermark(watermark: Watermark) {
        try {
            await sessionModule.chooseWatermark({
                sessionId: this.session.id,
                watermark,
            });
        } catch (error) {
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się zmienić znaku wodnego' });
        }
    }

    public async deleteSession() {
        if (await this.confirmationModel.confirm('Czy usunąć tę sesję?')) {
            this.deletingSession = true;
            try {
                if (!await sessionModule.deleteSession(this.session.id)) return;
                (this as any).$vToastify.success({ title: 'Sukces', body: 'Sesja została usunięta' });
                if (this.$route.name === 'editSession') {
                    this.$router.push({
                        name: 'sessionList',
                    });
                }
            } catch (error) {
                console.error('Error deleting session:', error);
                (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się usunąć sesji' });
            } finally {
                this.deletingSession = false;
            }
        }
    }

    @Watch('visible')
    public async onVisibilityChange(v: boolean) {
        if (v) {
            this.selectedPricelist = this.session.pricelistId;
            this.welcomeMessageSetId = this.session.welcomeMessageSetId;
            this.editedSessionName = this.session.name;
            this.editingSessionName = false;

            // Only show loading if we need to fetch data
            const needsLinks = this.links.length === 0;
            const needsPriceLists = this.priceLists.length === 0;
            const needsWelcomeMessageSets = !welcomeMessageSetsModule.loaded;

            if (needsLinks || needsPriceLists || needsWelcomeMessageSets) {
                this.loading = true;
                try {
                    if (needsLinks) {
                        const links = await api.getClientLinks(this.session.id);
                        this.links = links;
                    }
                    if (needsPriceLists) {
                        await priceListsModule.fetchPriceLists();
                    }
                    if (needsWelcomeMessageSets) {
                        await welcomeMessageSetsModule.fetchWelcomeMessageSets();
                    }
                } finally {
                    this.loading = false;
                }
            }

            this.syncBeforeAfterSelectionFromSession();
        }
    }

    @Watch('session.welcomeMessageSetId')
    public onSessionWelcomeMessageSetIdChanged(v: string | null) {
        this.welcomeMessageSetId = v;
        this.syncBeforeAfterSelectionFromSession();
    }

    @Watch('welcomeMessageSets')
    public onWelcomeMessageSetsChanged() {
        if (this.visible) {
            this.syncBeforeAfterSelectionFromSession();
        }
    }

    @Watch('beforeAfterMessagesDialog')
    public onBeforeAfterMessagesDialogChanged(v: boolean) {
        if (!v && this.selectedBeforeAfterMessageOption === this.createNewMessageOptionValue) {
            this.syncBeforeAfterSelectionFromSession();
        }
    }

    @Watch('selectedBeforeAfterMessageOption')
    public async onSelectedBeforeAfterMessageOptionChanged(v: string) {
        if (v === this.createNewMessageOptionValue) {
            this.beforeAfterMessages = {
                title: '',
                welcomeMessage: '',
                purchaseMessage: '',
            };
            this.beforeAfterMessagesDialog = true;
            return;
        }

        const selected = this.welcomeMessageSets.find((item) => item.id === v);
        if (!selected) {
            return;
        }

        this.beforeAfterMessages = {
            title: selected.title,
            welcomeMessage: selected.welcomeMessage,
            purchaseMessage: selected.purchaseMessage,
        };

        

        if (selected.id === this.welcomeMessageSetId) {
            return;
        }
        this.welcomeMessageSetId = selected.id;
        if (!this.visible) return;

        await this.assignWelcomeMessageSetToSession(selected.id);
    }

    public mounted() {
        this.chatEnabled = !!this.session.chatEnabled;
        this.welcomeMessageSetId = this.session.welcomeMessageSetId;
        this.editedSessionName = this.session.name;
        this.syncBeforeAfterSelectionFromSession();
    }

    public beforeDestroy() {
        if (this.highlightPricelistSelectionTimeout) {
            window.clearTimeout(this.highlightPricelistSelectionTimeout);
            this.highlightPricelistSelectionTimeout = null;
        }
    }

    @Watch('selectedPricelist')
    public async selectPricelist() {
        if (this.priceListSelectionPending) return;
        if (this.selectedPricelist === this.createNewPriceListOptionValue) {
            this.openCreateNewPricelistDialog();
            return;
        }

        if (this.selectedPricelist && this.selectedPricelist !== this.session.pricelistId) {
            const requestedId = this.selectedPricelist;
            this.priceListSelectionPending = true;
            try {
                await sessionModule.selectPricelist({
                    sessionId: this.session.id,
                    pricelistId: requestedId,
                });
            } catch (_) {
                if (this.selectedPricelist === requestedId) this.selectedPricelist = this.session.pricelistId;
                (this as any).$vToastify.error({ title: 'Nie udało się zmienić cennika sesji.' });
            } finally {
                this.priceListSelectionPending = false;
                if (this.selectedPricelist && this.selectedPricelist !== requestedId) this.selectPricelist();
            }
        }
    }

    public async priceListSaved(id: string) {
        await priceListsModule.fetchPriceLists();
        if (id === this.session.pricelistId) {
            try {
                await photosModule.refreshSessionSaleOptions(this.session.id);
            } catch (_) {
                (this as any).$vToastify.warning({ title: 'Cennik zapisany', body: 'Nie udało się odświeżyć ustawień zdjęć. Otwórz sesję ponownie.' });
            }
        }
        Vue.nextTick(() => this.selectedPricelist = id);
    }

    public onPricelistsDialogStateChanged(isOpen: boolean) {
        if (!isOpen && this.selectedPricelist === this.createNewPriceListOptionValue) {
            this.selectedPricelist = this.session.pricelistId;
        }
    }

    public async changeShareStatus(shareStatus: boolean) {
        this.links = this.links.map((l) => ({ ...l, disabled: !shareStatus }));
        if (shareStatus) {
            await api.enableSessionLinks(this.session.id);
            sessionModule.markSessionShared(this.session.id);
        } else {
            await api.disableSessionLinks(this.session.id);
            sessionModule.markSessionNotShared(this.session.id);
        }
    }

    public linksChanged(newLinks: ClientLink[]) {
        if (newLinks.length > this.links.length) {
            this.changeShareStatus(true);
        }
        this.links = newLinks;
    }

    public async deleteWatermark() {
        try {
            await sessionModule.deleteWatermark({ sessionId: this.session.id });
            this.watermarkDialog = false;
        } catch (error) {
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się wyłączyć znaku wodnego' });
        }
    }

    public async changeChatStatus(chatStatus: boolean) {
        this.chatEnabled = chatStatus;
        try {
            if (chatStatus) {
                await api.enableSessionChat(this.session.id);
                // Update the session's chat status in the store if necessary
                (this as any).$vToastify.success({ title: 'Sukces', body: 'Chat z klientem został włączony' });
            } else {
                await api.disableSessionChat(this.session.id);
                // Update the session's chat status in the store if necessary
                (this as any).$vToastify.success({ title: 'Sukces', body: 'Chat z klientem został wyłączony' });
            }
        } catch (error) {
            console.error('Error changing chat status:', error);
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się zmienić statusu chatu' });
            // Revert UI state if the API call fails
            this.chatEnabled = !chatStatus;
        }
    }

    public async updateBeforeAfterMessages(payload: BeforeAfterMessagesPayload) {
        const title = payload.title.trim();
        if (!title) {
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nazwa szablonu jest wymagana' });
            return;
        }

        const data = {
            title,
            welcomeMessage: payload.welcomeMessage,
            purchaseMessage: payload.purchaseMessage,
        };

        try {
            const existingId = this.selectedBeforeAfterMessageOption === this.createNewMessageOptionValue
                ? null
                : this.selectedBeforeAfterMessageOption;

            const saved: WelcomeMessageSetDto = existingId
                ? await welcomeMessageSetsModule.updateWelcomeMessageSet({ id: existingId, payload: data })
                : await welcomeMessageSetsModule.createWelcomeMessageSet(data);

            await welcomeMessageSetsModule.assignWelcomeMessageSet({
                sessionId: this.session.id,
                welcomeMessageSetId: saved.id,
            });

            this.beforeAfterMessages = {
                title: saved.title,
                welcomeMessage: saved.welcomeMessage,
                purchaseMessage: saved.purchaseMessage,
            };
            this.selectedBeforeAfterMessageOption = saved.id;
            this.welcomeMessageSetId = saved.id;
            sessionModule.updateSessionWelcomeMessageSet({
                sessionId: this.session.id,
                welcomeMessageSetId: saved.id,
            });

            this.beforeAfterMessagesDialog = false;
            (this as any).$vToastify.success({ title: 'Sukces', body: 'Wiadomości zostały zapisane' });
        } catch (error) {
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się zapisać wiadomości' });
        }
    }

    public async deleteBeforeAfterMessageSet() {
        const deletingId = this.selectedBeforeAfterMessageOption;
        if (!deletingId || deletingId === this.createNewMessageOptionValue) {
            this.beforeAfterMessages = {
                title: '',
                welcomeMessage: '',
                purchaseMessage: '',
            };
            return;
        }

        if (!await this.confirmationModel.confirm('Czy usunąć ten szablon wiadomości?')) {
            return;
        }

        this.loading = true;
        try {
            const result = await welcomeMessageSetsModule.deleteWelcomeMessageSet(deletingId);
            const detachedSessionIds = result?.detachedSessionIds || [];

            if (detachedSessionIds.length > 0) {
                await sessionModule.clearWelcomeMessageSetForSessions(detachedSessionIds);
            }

            if ((this.welcomeMessageSetId === deletingId || detachedSessionIds.includes(this.session.id))) {
                this.welcomeMessageSetId = null;
                sessionModule.updateSessionWelcomeMessageSet({
                    sessionId: this.session.id,
                    welcomeMessageSetId: null,
                });
            }

            this.beforeAfterMessages = {
                title: '',
                welcomeMessage: '',
                purchaseMessage: '',
            };
            this.selectedBeforeAfterMessageOption = this.createNewMessageOptionValue;
            this.beforeAfterMessagesDialog = false;

            (this as any).$vToastify.success({ title: 'Sukces', body: 'Szablon wiadomości został usunięty' });
        } catch (error) {
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się usunąć szablonu wiadomości' });
        } finally {
            this.loading = false;
        }
    }

    private syncBeforeAfterSelectionFromSession() {
        const assignedId = this.welcomeMessageSetId;
        if (!assignedId) {
            this.selectedBeforeAfterMessageOption = this.createNewMessageOptionValue;
            this.beforeAfterMessages = {
                title: '',
                welcomeMessage: '',
                purchaseMessage: '',
            };
            return;
        }

        const selected = this.welcomeMessageSets.find((item) => item.id === assignedId);
        if (!selected) {
            // Keep assigned id selected even before options are loaded.
            this.selectedBeforeAfterMessageOption = assignedId;
            return;
        }

        this.selectedBeforeAfterMessageOption = selected.id;
        this.beforeAfterMessages = {
            title: selected.title,
            welcomeMessage: selected.welcomeMessage,
            purchaseMessage: selected.purchaseMessage,
        };
    }

    private async assignWelcomeMessageSetToSession(welcomeMessageSetId: string | null) {
        const previousAssignedId = this.welcomeMessageSetId;

        try {
            await welcomeMessageSetsModule.assignWelcomeMessageSet({
                sessionId: this.session.id,
                welcomeMessageSetId,
            });

            this.welcomeMessageSetId = welcomeMessageSetId;
            await sessionModule.updateSessionWelcomeMessageSet({
                sessionId: this.session.id,
                welcomeMessageSetId,
            });
            (this as any).$vToastify.success({ title: 'Sukces', body: 'Szablon wiadomości został przypisany' });
        } catch (error) {
            this.welcomeMessageSetId = previousAssignedId;
            this.selectedBeforeAfterMessageOption = previousAssignedId || this.createNewMessageOptionValue;
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się przypisać szablonu wiadomości' });
        }
    }

    private getMessagePlainText(content: string) {
        return content
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
    }

    private openCreateNewPricelistDialog() {
        const pricelists = this.$refs.pricelistsDialog as any;
        if (pricelists && typeof pricelists.openCreateNew === 'function') {
            pricelists.openCreateNew();
        }
    }

    private flashPricelistSelection() {
        this.highlightPricelistSelection = false;
        if (this.highlightPricelistSelectionTimeout) {
            window.clearTimeout(this.highlightPricelistSelectionTimeout);
            this.highlightPricelistSelectionTimeout = null;
        }

        this.$nextTick(() => {
            this.highlightPricelistSelection = true;
            this.highlightPricelistSelectionTimeout = window.setTimeout(() => {
                this.highlightPricelistSelection = false;
                this.highlightPricelistSelectionTimeout = null;
            }, 1800);
        });
    }
}
